<template>
  <div id="data-list-list-view" class="datatable-container">
    <!-- Page Header -->
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-2xl font-semibold">{{ $t('nav.supplierEInvoicesLhdn') }}</h2>
      <div class="flex items-center">
        <vs-button
          color="primary"
          icon-pack="feather"
          icon="icon-download"
          class="mr-3"
          :disabled="loading || exporting"
          @click="exportCsv"
        >
          {{ exportLabel }}
        </vs-button>
        <a v-if="selected.length" class="mr-3 text-sm cursor-pointer text-primary" @click="selected = []">
          Clear selection
        </a>
        <vs-button
          color="primary"
          type="border"
          icon-pack="feather"
          icon="icon-refresh-cw"
          :disabled="loading"
          @click="getRecords"
        >
          {{ $t('dashboard.refresh') }}
        </vs-button>
      </div>
    </div>

    <p class="text-sm text-gray-600 mb-4">
      Supplier invoices received as the buyer, synced from MyInvois (LHDN). Open a
      document to view its full details and line items.
    </p>
    <p class="text-xs text-gray-500 mb-4">
      Tick the supplier e-invoices you want and click Export to download just those. With nothing ticked, Export
      downloads every document matching the filters below. One row per line item, with full supplier,
      buyer, payment and tax details, ready to import into your ERP.
    </p>

    <!-- Filters -->
    <vx-card>
      <div class="vx-row">
        <div class="vx-col md:w-1/4 w-full mb-3">
          <label class="text-sm">Document Type</label>
          <vs-select
            v-model="filters.typeName"
            class="w-full"
            @input="onFilterChange"
          >
            <vs-select-item :value="''" text="All Types" />
            <vs-select-item value="Invoice" text="Invoice" />
            <vs-select-item value="CreditNote" text="Credit Note" />
            <vs-select-item value="DebitNote" text="Debit Note" />
            <vs-select-item value="RefundNote" text="Refund Note" />
          </vs-select>
        </div>
        <div class="vx-col md:w-1/4 w-full mb-3">
          <label class="text-sm">Status</label>
          <vs-select
            v-model="filters.status"
            class="w-full"
            @input="onFilterChange"
          >
            <vs-select-item :value="''" text="All Statuses" />
            <vs-select-item value="Valid" text="Valid" />
            <vs-select-item value="Cancelled" text="Cancelled" />
            <vs-select-item value="Rejected" text="Rejected" />
          </vs-select>
        </div>
        <div class="vx-col md:w-1/4 w-full mb-3">
          <label class="text-sm">Issue Date From</label>
          <vs-input type="date" class="w-full" v-model="filters.issuedDateFrom" @change="onFilterChange" />
        </div>
        <div class="vx-col md:w-1/4 w-full mb-3">
          <label class="text-sm">Issue Date To</label>
          <vs-input type="date" class="w-full" v-model="filters.issuedDateTo" @change="onFilterChange" />
        </div>
        <div class="vx-col md:w-1/2 w-full mb-3">
          <label class="text-sm">Search (document # / supplier / TIN)</label>
          <vs-input
            class="w-full"
            icon-pack="feather"
            icon="icon-search"
            v-model="filters.search"
            @keyup.enter="onFilterChange"
          />
        </div>
      </div>
    </vx-card>

    <!-- Results Table -->
    <vx-card class="mt-5">
      <h4 class="card-title">{{ $t('dashboard.results') }}</h4>

      <vs-table
        v-show="loaded"
        stripe
        sst
        @sort="handleSort"
        ref="table"
        :data="documents"
        :noDataText="$t('dashboard.noDataAvailable')"
      >
        <template slot="thead">
          <vs-th class="select-col">
            <vs-checkbox v-model="pageSelected" title="Select all on this page" />
          </vs-th>
          <vs-th sort-key="internalId">Document #</vs-th>
          <vs-th>Type</vs-th>
          <vs-th sort-key="supplierName">Supplier</vs-th>
          <vs-th sort-key="dateTimeIssued">Issue Date</vs-th>
          <vs-th sort-key="totalPayableAmount">Payable</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>{{ $t('dashboard.action') }}</vs-th>
        </template>

        <template slot-scope="{ data }">
          <tbody>
            <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
              <vs-td class="select-col">
                <vs-checkbox v-model="selected" :vs-value="tr.uuid" />
              </vs-td>
              <vs-td>
                <span
                  class="font-medium cursor-pointer text-primary"
                  @click="handleView(tr)"
                >
                  {{ tr.internalId || '—' }}
                </span>
              </vs-td>
              <vs-td>
                <vs-chip :color="typeColor(tr.typeName)">{{
                  typeLabel(tr.typeName)
                }}</vs-chip>
              </vs-td>
              <vs-td>
                <p class="text-sm">{{ tr.supplierName || '—' }}</p>
                <p class="text-xs text-gray-500">{{ tr.supplierTIN }}</p>
              </vs-td>
              <vs-td>
                <span v-if="tr.dateTimeIssued" class="text-sm">{{
                  tr.dateTimeIssued | moment('DD MMM YYYY')
                }}</span>
                <span v-else class="text-gray-400">—</span>
              </vs-td>
              <vs-td>
                <span class="text-sm">{{
                  formatAmount(tr.totalPayableAmount, tr.documentCurrency)
                }}</span>
              </vs-td>
              <vs-td>
                <vs-chip :color="statusColor(tr.status)">{{ tr.status }}</vs-chip>
              </vs-td>
              <vs-td>
                <feather-icon
                  icon="EyeIcon"
                  svgClasses="w-5 h-5 hover:text-primary stroke-current"
                  class="cursor-pointer"
                  @click.stop="handleView(tr)"
                />
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>

      <div v-if="loading" class="flex justify-center items-center py-10">
        <vs-spinner color="primary" />
      </div>

      <div v-if="loaded && documents.length === 0" class="text-center py-10">
        <feather-icon
          icon="InboxIcon"
          svgClasses="w-12 h-12 text-gray-400 mx-auto mb-4"
        />
        <p class="text-gray-500">No supplier e-invoices found</p>
      </div>

      <template v-if="totalPage > 1">
        <div class="flex justify-end mt-4">
          <vs-pagination :total="totalPage" v-model="currentPage"></vs-pagination>
        </div>
      </template>
    </vx-card>
  </div>
</template>

<script>
import serverSort from '@/mixins/server-sort'
import { saveBlob, filenameFromResponse, blobErrorMessage } from '@/utils/downloadFile'

// Selected keys travel in the export URL; keep it well under server URL limits.
const EXPORT_SELECTION_LIMIT = 200

export default {
  mixins: [serverSort],
  data() {
    return {
      itemsPerPage: 25,
      loaded: false,
      loading: false,
      currentPage: 1,
      totalItem: 0,
      totalPage: 0,
      // Server sort — the /ap-documents endpoint takes sortBy (an allowlisted
      // ApDocumentSortField) + sortOrder, not the crud "field,DIR" string.
      sortBy: { dateTimeIssued: 'DESC' },
      defaultSortBy: { dateTimeIssued: 'DESC' },
      exporting: false,
      // Ticked document keys, kept across pages and filter changes.
      selected: [],
      filters: {
        typeName: '',
        status: '',
        search: '',
        issuedDateFrom: '',
        issuedDateTo: '',
      },
    }
  },
  computed: {
    pageKeys() {
      return (this.documents || []).map(d => d.uuid).filter(Boolean)
    },
    pageSelected: {
      get() {
        return this.pageKeys.length > 0 && this.pageKeys.every(k => this.selected.includes(k))
      },
      set(value) {
        const others = this.selected.filter(k => !this.pageKeys.includes(k))
        this.selected = value ? [...others, ...this.pageKeys] : others
      },
    },
    exportLabel() {
      if (this.exporting) return 'Exporting…'
      return this.selected.length ? `Export ${this.selected.length} selected` : 'Export all (CSV)'
    },
    documents() {
      return this.$store.getters['apDocuments/documents']
    },
  },
  watch: {
    currentPage(newValue, oldValue) {
      if (newValue !== oldValue) this.getRecords()
    },
  },
  created() {
    this.getRecords()
  },
  methods: {
    async getRecords() {
      this.loading = true
      try {
        const [sortField, sortOrder] = Object.entries(this.sortBy)[0] || ['dateTimeIssued', 'DESC']
        const params = {
          page: this.currentPage,
          limit: this.itemsPerPage,
          sortBy: sortField,
          sortOrder,
          ...this.filterParams(),
        }

        await this.$store.dispatch('apDocuments/fetchApDocuments', params)
        const pagination = this.$store.state.apDocuments.pagination || {}
        this.currentPage = pagination.currentPage || 1
        this.totalItem = pagination.totalItem || this.documents.length
        this.totalPage = pagination.totalPage || 1
        this.loaded = true
      } catch (e) {
        console.error('[ApDocuments] fetch failed:', e)
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text:
            (e.response && e.response.data && e.response.data.message) ||
            'Failed to fetch AP documents',
        })
      } finally {
        this.loading = false
      }
    },

    // Active filters only — shared by the list and the CSV export.
    filterParams() {
      return Object.fromEntries(Object.entries(this.filters).filter(([, v]) => v))
    },

    async exportCsv() {
      this.exporting = true
      try {
        if (this.selected.length > EXPORT_SELECTION_LIMIT) {
          this.$vs.notify({
            color: 'warning',
            title: 'Too many selected',
            text: `Select up to ${EXPORT_SELECTION_LIMIT} documents, or clear the selection to export everything matching the filters.`,
          })
          return
        }
        const params = this.selected.length ? { uuids: this.selected.join(',') } : this.filterParams()
        const response = await this.$store.dispatch('apDocuments/exportApDocumentsCsv', params)
        saveBlob(response.data, filenameFromResponse(response, 'supplier-einvoices-lhdn.csv'))
      } catch (e) {
        console.error('[ApDocuments] export failed:', e)
        this.$vs.notify({
          color: 'danger',
          title: 'Export failed',
          text: await blobErrorMessage(e, 'Could not export documents. Please try again.'),
        })
      } finally {
        this.exporting = false
      }
    },

    onFilterChange() {
      this.currentPage = 1
      this.getRecords()
    },

    handleView(item) {
      if (!item || !item.uuid) return
      this.$router.push('/ap-documents/' + item.uuid).catch(() => {})
    },

    typeLabel(type) {
      return (
        {
          Invoice: 'Invoice',
          CreditNote: 'Credit Note',
          DebitNote: 'Debit Note',
          RefundNote: 'Refund Note',
        }[type] || type
      )
    },
    typeColor(type) {
      return (
        {
          Invoice: 'primary',
          CreditNote: 'warning',
          DebitNote: 'warning',
          RefundNote: 'dark',
        }[type] || 'primary'
      )
    },
    statusColor(status) {
      return (
        {
          Valid: 'success',
          Cancelled: 'warning',
          Rejected: 'danger',
        }[status] || 'primary'
      )
    },
    formatAmount(amount, currency) {
      if (amount == null || isNaN(amount)) return '—'
      const value = Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return currency ? `${currency} ${value}` : value
    },
  },
}
</script>

<style lang="scss" scoped>
.select-col {
  width: 2.5rem;
}
.datatable-container {
  .vs-table--tbody-table {
    .vs-table--td {
      padding: 12px 8px;
    }
  }
}
</style>
